<script setup lang="ts">
import { ref, onMounted } from 'vue';
import FeatureWalkthroughSteps from '@/components/features/walkthrough/FeatureWalkthroughSteps.vue';
import ConstructionMock from '@/components/features/mocks/construction/ConstructionMock.vue';
import EditorMock from '@/components/features/mocks/editor/EditorMock.vue';
import SharingMock from '@/components/features/mocks/sharing/SharingMock.vue';
import StartButton from '@/components/button/StartButton.vue';
import Footer from '@/components/Footer.vue';

// Template Images
import officeImg from '@/assets/templates/office-city-view.png';
import whiteboardImg from '@/assets/templates/whiteboard.png';
import matrixImg from '@/assets/templates/matrix.png';
import bet365Img from '@/assets/templates/bet365.png';
import speechImg from '@/assets/templates/speech-bubbles.png';
import blackboardImg from '@/assets/templates/blackboard-menu.png';

// Intersection Observer for scroll animations
onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active');
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.animate-on-scroll, .scroll-reveal').forEach((el) => {
    observer.observe(el);
  });
});

const constructionSteps = [
  { title: "Smart Text", desc: "Express your bet naturally. Just start typing events and selections.", typed: "Arsenal", placeholder: "Type your bet...", ghost: "", showInsight: false, expanded: false, resolved: false },
  { title: "Dynamic Insights", desc: "Watch as we resolve events, teams and markets as you type in real-time.", typed: "Arsenal", placeholder: "", ghost: "", showInsight: true, expanded: false, resolved: false },
  { title: "More Information", desc: "Expand to show additional information, listing matches, to help build a bet.", typed: "Arsenal", placeholder: "", ghost: "", showInsight: true, expanded: true, resolved: true },
  { title: "Smart Append", desc: "Simply continue typing to add more events and selections, we'll do the rest.", typed: "Arsenal and Saka to score", placeholder: "", ghost: "", showInsight: false, expanded: false, resolved: true }
];
const activeConstructionStep = ref(0);

const editingSteps = [
  { title: "Overview", desc: "See your bet selections at a glance.", view: 'main' as const },
  { title: "Fine Tuning", desc: "Fine-tune your bet selections.", view: 'alter-market' as const },
  { title: "Add Events", desc: "Easily append more events to your bet slip.", view: 'add-more' as const },
  { title: "Add Markets", desc: "Easily add additional markets to your current events.", view: 'add-market' as const }
];
const activeEditingStep = ref(0);

const sharingSteps = [
  { title: "Image Preview", desc: "Make your bet slip a picture.", tab: 'preview' as const },
  { title: "Templates", desc: "Choose from dozens of templates in library.", tab: 'templates' as const },
  { title: "Share with Friends", desc: "Message or post via social media platforms. Anyone tap link to open your bet slip.", tab: 'preview' as const, showOverlay: true }
];
const activeSharingStep = ref(0);

const selectedTemplate = ref(0);
const templates = [
  { name: 'Office City View', bg: '#09090b', accent: '#e21b0d', logo: 'white', image: officeImg },
  { name: 'Whiteboard', bg: '#e21b0d', accent: '#ffffff', logo: 'red', image: whiteboardImg },
  { name: 'Matrix', bg: 'rgba(255,255,255,0.05)', accent: '#e21b0d', logo: 'white', image: matrixImg },
  { name: 'Bet365', bg: '#09090b', accent: '#e21b0d', logo: 'white', image: bet365Img },
  { name: 'Speech Bubbles', bg: '#e21b0d', accent: '#ffffff', logo: 'red', image: speechImg },
  { name: 'Blackboard Menu', bg: 'rgba(255,255,255,0.05)', accent: '#e21b0d', logo: 'white', image: blackboardImg }
];

const selectTemplate = (idx: number) => {
  selectedTemplate.value = idx;
};

const constructionSection = ref<HTMLElement | null>(null);
const editorSection = ref<HTMLElement | null>(null);
const sharingSection = ref<HTMLElement | null>(null);

const scrollToSection = (el: HTMLElement | null) => {
  if (el) {
    const mobileTabs = el.querySelector('.mobile-step-tabs');
    const target = (window.innerWidth <= 768 && mobileTabs) ? mobileTabs : el;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const scrollToEditor = () => {
  scrollToSection(editorSection.value);
};
</script>

<template>
  <div class="features-page">
    <!-- Header -->
    <header class="features-header animate-on-scroll">
      <div class="badge">FEATURES</div>
      <h1>How <span class="text-accent">To Use</span></h1>
      <p>Simple and fast to create an image of your betting prediction to share.</p>
    </header>


    <!-- Section 1: Natural Expression -->
    <section ref="constructionSection" class="feature-section scroll-reveal">
      <div class="section-container">
        <div class="content">
          <FeatureWalkthroughSteps 
            v-model="activeConstructionStep"
            :steps="constructionSteps"
            badge="CAPTURING"
            title="Express"
            accentTitle="Your Bet"
            @step-click="scrollToSection(constructionSection)"
          />
        </div>
        <div class="visual animate-on-scroll flex justify-center">
          <ConstructionMock 
            v-model:activeStep="activeConstructionStep" 
            :steps="constructionSteps" 
            @scrollToEditor="scrollToEditor"
          />
        </div>
      </div>
    </section>

    <!-- Section 2: Precision Editing -->
    <section ref="editorSection" id="editor-section" class="feature-section alt scroll-reveal">
      <div class="section-container">
        <!-- Visual first on mobile, then tabs -->
        <div class="visual animate-on-scroll flex fill-height">
          <EditorMock 
            :activeStep="activeEditingStep" 
            @update:activeStep="(val) => { activeEditingStep = val; scrollToSection(editorSection); }" 
            :steps="editingSteps" 
          />
        </div>
        <div class="content">
          <FeatureWalkthroughSteps 
            v-model="activeEditingStep"
            :steps="editingSteps"
            badge="ADJUSTMENTS"
            title="Selection"
            accentTitle="Refinement"
            @step-click="scrollToSection(editorSection)"
          />
        </div>
      </div>
    </section>

    <!-- Section 3: Viral Sharing -->
    <section ref="sharingSection" class="feature-section scroll-reveal">
      <div class="section-container">
        <div class="content">
          <FeatureWalkthroughSteps 
            v-model="activeSharingStep"
            :steps="sharingSteps"
            badge="SHARING WITH FRIENDS"
            title="Message Chats"
            accentTitle="Social Media"
            @step-click="scrollToSection(sharingSection)"
          />
        </div>
        <div class="visual animate-on-scroll">
          <SharingMock 
            :activeStep="activeSharingStep" 
            @update:activeStep="(val) => { activeSharingStep = val; scrollToSection(sharingSection); }"
            :steps="sharingSteps" 
            :selectedTemplate="selectedTemplate"
            :templates="templates"
            @select-template="selectTemplate"
          />
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="final-cta scroll-reveal">
      <h2 class="animate-on-scroll">Create and Share <span class="text-gradient">Start the Fun!</span></h2>
      <div class="flex justify-center">
        <StartButton />
      </div>
    </section>

    <Footer />
  </div>
</template>

<style scoped>
.features-page { width: 100%; background: var(--bg-dark); }
.features-header { padding: 120px 5% 40px; text-align: center; }
.features-header h1 { font-size: clamp(2.5rem, 8vw, 4.5rem); font-weight: 900; margin-bottom: 24px; color: #fff; }
.features-header p { color: var(--text-secondary); font-size: 1.2rem; }
.feature-section { padding: 100px 5%; }
.feature-section.alt { background: rgba(255, 255, 255, 0.02); }
.content { scroll-margin-top: 80px; }
.section-container { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1.8fr; gap: 80px; align-items: stretch; }

.final-cta { padding: 120px 5%; text-align: center; background: radial-gradient(circle at center, rgba(226, 27, 13, 0.15) 0%, transparent 70%); }
.final-cta h2 { font-size: 3rem; font-weight: 900; margin-bottom: 40px; color: #fff; }


@media (max-width: 1024px) { 
  .section-container { grid-template-columns: 1fr; gap: 20px; } 
  .feature-section.alt .visual { order: 2; }
  .feature-section.alt .content { order: 1; }
  .visual { width: 100%; display: flex; justify-content: center; }
}

/* Ensure the alt section (Editor) has the same column proportions as the others on Desktop */
@media (min-width: 1025px) {
  .feature-section.alt .section-container {
    grid-template-columns: 1.8fr 1fr;
  }
}

@media (max-width: 768px) {
  .features-header { padding: 80px 5% 40px; }
  .features-header h1 { font-size: 2.5rem; }
  .feature-section { padding: 60px 5%; }
  .final-cta h2 { font-size: 2rem; }

  .content { text-align: center; display: flex; flex-direction: column; align-items: center; }
}
</style>
